import { memo } from "react";
import { belowPreviewBanner } from "@/lib/previewBannerOffset";
import type { BotezBokehTemplateProps } from "./types";
import { FONT_DISPLAY, FONT_KICKER, TEAL, TEAL_DARK, getEventKind, isRsvpCutoffPassed } from "./utils";

/**
 * Floating pill navbar from the prototype: a single rounded container with the
 * short name of the celebrated child (or couple) and an RSVP button. It sits
 * below the dialog layer (z-50) and below the draft-preview banner (JUB-154).
 */
export const BotezBokehNavbar = memo(({ event, t, onRsvpClick }: BotezBokehTemplateProps) => {
  const kind = getEventKind(event);
  // JUB-175: show the full name (truncated by CSS) instead of the first word only.
  const shortName =
    (kind.isBaptismOnly && event.starBaptism
      ? event.starBaptism
      : event.starBride
        ? event.starBride
        : event.starBaptism
          ? event.starBaptism
          : event.name) || event.name;

  const rsvpVisible = Boolean(event.rsvpSettings?.rsvpEnabled) && !isRsvpCutoffPassed(event);

  return (
    <nav
      className="fixed left-0 right-0 z-40 px-6"
      style={{ top: belowPreviewBanner("max(1rem, calc(env(safe-area-inset-top) + 0.5rem))") }}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-center min-w-0">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 max-w-full px-4 py-2 rounded-full bg-white/95 backdrop-blur-sm shadow-lg">
          <a
            href="#home"
            className="text-xl tracking-wider px-2 font-bold min-w-0 flex-1 truncate"
            style={{ fontFamily: FONT_DISPLAY, color: TEAL }}
          >
            {shortName}
          </a>

          {rsvpVisible && (
            <button
              type="button"
              onClick={() => {
                if (onRsvpClick) {
                  onRsvpClick();
                } else {
                  document.getElementById("rsvp")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }}
              className="shrink-0 whitespace-nowrap px-3 sm:px-5 py-2 rounded-full text-white text-xs uppercase tracking-[0.12em] transition-colors duration-300 font-medium"
              style={{ fontFamily: FONT_KICKER, backgroundColor: TEAL }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = TEAL_DARK)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = TEAL)}
            >
              {t("events.form.template.rsvp")}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
});

BotezBokehNavbar.displayName = "BotezBokehNavbar";
